import { useEffect, useCallback, useMemo, useRef } from "react";

import { useObjectMemo } from "byzantine";

import type { OnboardingPrompt } from "byzantine/src/dbbl/businessLogic/modules/onboarding";
import type { OnboardingGuideState } from "../../../guides/storage";

const MAX_VISIBLE_STACK = 3;
const STACK_OFFSET = 3;
const STACK_INSET = 6;
const CARD_HEIGHT = 138;

export const CAUGHT_UP_SENTINEL = "__caught_up__";

export type StackEntry = OnboardingPrompt | typeof CAUGHT_UP_SENTINEL;

export const useCardStack = ({
  visiblePrompts,
  rawPrompts,
  dismissingUuid,
  guideState,
}: {
  visiblePrompts: OnboardingPrompt[];
  rawPrompts: OnboardingPrompt[];
  dismissingUuid: string | null;
  guideState: OnboardingGuideState;
}) => {
  const stackedPrompts = useMemo(() => {
    const limit = dismissingUuid ? MAX_VISIBLE_STACK + 1 : MAX_VISIBLE_STACK;
    const cards: StackEntry[] = [...visiblePrompts.slice(0, limit)];

    if (
      rawPrompts.length > 0 &&
      visiblePrompts.length < limit &&
      !guideState.caughtUpDismissed
    ) {
      cards.push(CAUGHT_UP_SENTINEL);
    }
    return cards;
  }, [
    visiblePrompts,
    rawPrompts,
    dismissingUuid,
    guideState.caughtUpDismissed,
  ]);

  const caughtUpIsTop =
    stackedPrompts.length > 0 && stackedPrompts[0] === CAUGHT_UP_SENTINEL;

  // Track which UUIDs were previously in the stack to detect new entries
  const prevStackedUuidsRef = useRef<Set<string>>(new Set());
  const currentUuids = useMemo(
    () =>
      new Set(
        stackedPrompts
          .filter((p): p is OnboardingPrompt => p !== CAUGHT_UP_SENTINEL)
          .map((p) => p.uuid)
      ),
    [stackedPrompts]
  );

  const newEntryUuids = useMemo(() => {
    const newUuids = new Set<string>();
    Array.from(currentUuids).forEach((uuid) => {
      if (!prevStackedUuidsRef.current.has(uuid)) {
        newUuids.add(uuid);
      }
    });
    return newUuids;
  }, [currentUuids]);

  useEffect(() => {
    prevStackedUuidsRef.current = currentUuids;
  }, [currentUuids]);

  const getCardStyle = useCallback(
    (index: number, isBeingDismissed: boolean) => {
      const effectiveIndex =
        dismissingUuid && !isBeingDismissed ? index - 1 : index;
      const topOffset = effectiveIndex * STACK_OFFSET;
      const horizontalInset = effectiveIndex * STACK_INSET;
      const zIndex = isBeingDismissed
        ? MAX_VISIBLE_STACK + 1
        : MAX_VISIBLE_STACK - effectiveIndex;

      return {
        position: "absolute" as const,
        top: topOffset,
        left: horizontalInset,
        right: horizontalInset,
        height: CARD_HEIGHT,
        zIndex,
        transition: "all 0.2s ease",
      };
    },
    [dismissingUuid]
  );

  return useObjectMemo({
    stackedPrompts,
    caughtUpIsTop,
    newEntryUuids,
    getCardStyle,
  });
};
